/**
 * Catches a render crash inside a portal's content so it shows a recoverable
 * message instead of blanking the whole app.
 *
 * Wraps the routed content only (the shell and navigation stay usable), and
 * resets when the route changes so moving away from a broken page recovers.
 */
import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";

interface RouteErrorBoundaryProps {
  children: ReactNode;
  /** Change this (e.g. the pathname) to clear a caught error. */
  resetKey: string;
}

interface RouteErrorBoundaryState {
  error: Error | null;
}

export class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  componentDidUpdate(previous: RouteErrorBoundaryProps) {
    if (previous.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    /* No PII here: the error and the component stack only. */
    console.error("Route render failed", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div role="alert" className="mx-auto mt-10 flex max-w-[480px] flex-col items-center gap-3 rounded-xl border border-border bg-card px-8 py-10 text-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-destructive-background">
          <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden />
        </span>
        <h1 className="text-base font-bold text-foreground">This page hit a problem</h1>
        <p className="text-sm text-muted-foreground">Nothing was lost. Try again, or go back to your board.</p>
        <Button size="sm" onClick={() => this.setState({ error: null })}>
          Try again
        </Button>
      </div>
    );
  }
}
